import { useCallback, useMemo } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { GraphQL } from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'
import { Delete02Icon, PlusSignIcon } from '@hugeicons/core-solid-rounded'
import { useFieldArray } from 'react-hook-form'

import * as Form from '@/features/form'
import Input from '@/components/ui/Input'
import Dropdown, { Option } from '@/components/ui/Dropdown'
import { SelectDoor } from '@/features/integrationDoor'
import { FileUpload } from '@/features/files'
import { FormData } from '../hooks/useUploadButtonRow'
import {
  FileViewIcon,
  KeyIcon,
  LinkSquare01Icon,
  MapingIcon
} from '@hugeicons/core-stroke-rounded'

function CustomButtonInput({
  index,
  name,
  typeOptions,
  iconOptions,
  remove
}: {
  index: number
  name: string
  iconOptions: Option<string>[]
  typeOptions: Option<string>[]
  remove: () => void
}) {
  const { t } = useLingui()

  return (
    <div className="flex flex-row gap-2 w-full bg-primary/10 rounded-small p-2.5 items-start relative">
      <div className="flex flex-col gap-1 flex-1">
        <Form.Field name={`${name}.type`} label={t`Type`}>
          {({ renderProps }) => (
            <Dropdown options={typeOptions} {...renderProps} />
          )}
        </Form.Field>
        <div className="flex flex-row items-start gap-2 justify-evenly">
          <div className="flex-1">
            <Form.Field name={`${name}.icon`} label={t`Icon`}>
              {({ renderProps }) => (
                <Dropdown options={iconOptions} {...renderProps} />
              )}
            </Form.Field>
          </div>
          <div className="flex-1">
            <Form.Field
              name={`${name}.label`}
              translationPrefix={`items.${index}`}
              label={t`Label`}
              rules={{
                maxLength: {
                  value: 255,
                  message: t`Can't be longer than 255 characters.`
                },
                validate: {
                  required: Form.Validations.requiredWithTrimValidation(
                    t`This field is required`
                  )
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  type="text"
                  {...props}
                  placeholder={t`Label`}
                  onChange={event => onChange(event.target.value)}
                />
              )}
            </Form.Field>
          </div>
        </div>
        <Form.Data<FormData>>
          {({ items }) => {
            const item = items[index]

            if (!item) return null

            switch (item.type) {
              case GraphQL.ButtonRowItemType.OpenDoor:
                return (
                  <Form.Field
                    resetOnUnmount
                    name={`${name}.integrationDoorId`}
                    label={t`Connected Door`}
                    rules={{
                      validate: {
                        required: Form.Validations.requiredNumber(
                          t`This field is required`
                        )
                      }
                    }}
                  >
                    {({ renderProps }) => <SelectDoor {...renderProps} />}
                  </Form.Field>
                )

              case GraphQL.ButtonRowItemType.File:
                return (
                  <Form.Field
                    name={`${name}.file`}
                    label={t`File`}
                    rules={{
                      required: t`This field is required`
                    }}
                  >
                    {({ locale, isDefaultLocale, renderProps }) => (
                      <div className="flex flex-row justify-center w-full">
                        <FileUpload
                          locale={locale}
                          isDefaultLocale={isDefaultLocale}
                          placeholder={t`Upload File`}
                          className="aspect-square"
                          {...renderProps}
                        />
                      </div>
                    )}
                  </Form.Field>
                )

              case GraphQL.ButtonRowItemType.Link:
                return (
                  <Form.Field
                    name={`${name}.link`}
                    label={t`Link`}
                    rules={{
                      validate: {
                        required: Form.Validations.requiredWithTrimValidation(
                          t`This field is required`
                        ),
                        url: Form.Validations.urlValidation(
                          t`This field has to be a valid URL`
                        )
                      }
                    }}
                  >
                    {({ renderProps: { onChange, ...props } }) => (
                      <Input
                        type="url"
                        {...props}
                        placeholder={t`Link`}
                        onChange={event => onChange(event.target.value)}
                      />
                    )}
                  </Form.Field>
                )

              default:
                return null
            }
          }}
        </Form.Data>
        <button
          type="button"
          onClick={remove}
          className="absolute right-2 top-2"
        >
          <HugeiconsIcon icon={Delete02Icon} className="size-5 text-red-500" />
        </button>
      </div>
    </div>
  )
}

type Props = {
  name: string
  showDoorSelector: boolean
}

export function ButtonRowItems({ name, showDoorSelector }: Props) {
  const { t } = useLingui()

  const { fields, append, remove } = useFieldArray({
    name,
    shouldUnregister: true
  })

  const onAdd = useCallback(() => {
    append({
      type: GraphQL.ButtonRowItemType.Link,
      icon: GraphQL.ButtonRowItemIcon.Link,
      label: ''
    })
  }, [append])

  const typeOptions = useMemo(() => {
    const types = [
      {
        value: GraphQL.ButtonRowItemType.Link,
        label: t`Link`
      },
      {
        value: GraphQL.ButtonRowItemType.File,
        label: t`File`
      }
    ]

    if (showDoorSelector) {
      types.push({
        value: GraphQL.ButtonRowItemType.OpenDoor,
        label: t`Open Door`
      })
    }

    return types
  }, [t, showDoorSelector])

  const iconOptions = useMemo(() => {
    const types = [
      {
        value: GraphQL.ButtonRowItemIcon.Link,
        label: t`Link`,
        Icon: LinkSquare01Icon
      },
      {
        value: GraphQL.ButtonRowItemIcon.File,
        label: t`File`,
        Icon: FileViewIcon
      },
      {
        value: GraphQL.ButtonRowItemIcon.Key,
        label: t`Key`,
        Icon: KeyIcon
      },
      {
        value: GraphQL.ButtonRowItemIcon.Map,
        label: t`Map`,
        Icon: MapingIcon
      }
    ]

    return types
  }, [t])

  return (
    <div className="flex flex-col gap-4">
      {fields.map(function (field, index) {
        return (
          <CustomButtonInput
            key={field.id}
            name={`${name}.${index}`}
            index={index}
            remove={() => remove(index)}
            typeOptions={typeOptions}
            iconOptions={iconOptions}
          />
        )
      })}
      <button
        onClick={onAdd}
        type="button"
        className="flex flex-row justify-center items-center p-2 border border-dashed border-text-light rounded-small gap-1"
      >
        <HugeiconsIcon icon={PlusSignIcon} className="size-5 text-text-light" />
        <span className="text-text-light text-regular">
          <Trans>Add Button</Trans>
        </span>
      </button>
    </div>
  )
}
